import React, { Suspense, useCallback, useMemo, useState } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { defineMessage, FormattedMessage, useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
import { useFlags } from '@atlaskit/flag';
import AiChatIcon from '@atlaskit/icon/core/ai-chat';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { fg } from '@atlaskit/platform-feature-flags';
import { useRovoAgentActionAnalytics } from '@atlaskit/rovo-agent-analytics/actions';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import {
	JIRA_INLINE_AGENT_CREATION_AGENT_ASSIGNED_EVENT,
	Topics,
} from '@atlaskit/rovo-triggers/types';
import { getAtlassianStudioAgentEditUrl } from '@atlassian/conversation-assistant-agent/common/utils/atlassian-studio-url';
import type { Knowledge } from '@atlassian/conversation-assistant-agent/ui/KnowledgePillList';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import { ExperienceStart } from '@atlassian/conversation-assistant-instrumentation/ui/ExperienceTracker';
import type { ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type {
	StudioCreateAgentExecutedData,
	StudioCreateAgentKnowledgeSourceConfig,
	StudioCreateAgentPayload,
	StudioCreateAgentTool,
} from '@atlassian/conversation-assistant-message-actions-api/studio';
import type { ActionRendererFor } from '@atlassian/conversation-assistant-message-actions-api/types';
import { ChatGenericError } from '@atlassian/conversation-assistant-ui-components/ui/ChatGenericError';

import { AgentCreationFollowUpText, type FollowUpResult } from './AgentCreationFollowUpText';
import { AgentPlanAnalytics } from './AgentPlanAnalytics';
import { getCreateAgentAnalyticsAttributes } from './analytics/getCreateAgentAnalyticsAttributes';
import { getPlanAnalyticsAttributes } from './analytics/getPlanAnalyticsAttributes';
import { GASV3_ANALYTICS_TOUCHPOINT_NAME, STUDIO_NAVIGATION_SOURCE } from './constants';
import { CreateAgentCard } from './create-agent-card/CreateAgentCard';
import { CreateAgentCardSkeleton } from './create-agent-card/CreateAgentCardSkeleton';
import { CreateAgentCardWithQuery } from './create-agent-card/CreateAgentCardWithQuery';
import type { Tool } from './create-agent-card/ExpandableBody';
import { AssignAgentError, useAssignAgent } from './useAssignAgent';
import { useInvokeAgent } from './useInvokeAgent';
import { getAgentIdFromAri } from './utils';

const refineInStudioLabel = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.studio-create-agent.StudioCreateAgentRenderer.refineInStudioLabel',
	defaultMessage: 'Refine in Studio',
	description:
		'Secondary action button label on the inline create-agent card. Opens the agent plan in Atlassian Studio for further refinement.',
});

const viewInStudioLabel = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.studio-create-agent.StudioCreateAgentRenderer.viewInStudioLabel',
	defaultMessage: 'View in Studio',
	description:
		'Secondary action button label on the inline create-agent card after a build-and-chat agent has been published. Opens the published agent in Atlassian Studio.',
});

const assignAgentLabel = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.studio-create-agent.StudioCreateAgentRenderer.assignAgentLabel',
	defaultMessage: 'Assign agent',
	description:
		'Primary action button label on the inline create-agent card for the "issue_assign" flow — persists the agent and assigns it to the originating Jira issue.',
});

const buildAndInvokeLabel = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.studio-create-agent.StudioCreateAgentRenderer.buildAndInvokeLabel',
	defaultMessage: 'Start session',
	description:
		'Primary action button label on the inline create-agent card for the "issue_invoke" flow — persists the agent and starts a Rovo agent session on the originating Jira issue.',
});

const buildAndChatLabel = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.studio-create-agent.StudioCreateAgentRenderer.buildAndChatLabel',
	defaultMessage: 'Start chat',
	description:
		'Primary action button label on the inline create-agent card for the build-and-chat flow — publishes the agent and opens a new Rovo chat with it selected.',
});

const chatWithAgentLabel = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.studio-create-agent.StudioCreateAgentRenderer.chatWithAgentLabel',
	defaultMessage: 'Chat with agent',
	description:
		'Primary action button label on the inline create-agent card after a build-and-chat agent has been published. Opens a new Rovo chat with the agent selected.',
});

const buildLabel = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.studio-create-agent.StudioCreateAgentRenderer.buildLabel',
	defaultMessage: 'Build',
	description:
		'Primary action button label on the inline create-agent card. For the fallback non-Jira issue flow it persists the agent as a draft; for the build-and-chat flow it builds and publishes the agent before chat starts.',
});

const buildAgentLabel = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.studio-create-agent.StudioCreateAgentRenderer.buildAgentLabel',
	defaultMessage: 'Build agent',
	description:
		'Primary action button label on the inline create-agent card for the first step of the multi-step build-and-chat flow. Builds and publishes the agent before chat starts.',
});

const assigningFlagTitle = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.studio-create-agent.StudioCreateAgentRenderer.assigningFlagTitle',
	defaultMessage: 'Agent created',
	description: 'Flag title shown while assigning the agent to the work item.',
});

const assigningFlagDescription = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.studio-create-agent.StudioCreateAgentRenderer.assigningFlagDescription',
	defaultMessage: "It's being assigned to the work item. This may take a minute or two.",
	description: 'Flag description shown while assigning the agent to the work item.',
});

const assignedFlagTitle = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.studio-create-agent.StudioCreateAgentRenderer.assignedFlagTitle',
	defaultMessage: 'Agent assigned',
	description: 'Flag title shown after the agent is successfully assigned.',
});

const assignedFlagDescription = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.studio-create-agent.StudioCreateAgentRenderer.assignedFlagDescription',
	defaultMessage: "We've assigned it to the work item.",
	description: 'Flag description shown after the agent is successfully assigned.',
});

/** Values sent for `chosenOption`, matching the `actionTaken` field on `executedData`. */
const CHOSEN_OPTION = {
	BUILD_DRAFT: 'build_draft',
	BUILD_AND_APPLY: 'build_and_apply',
	BUILD_AND_CHAT: 'build_and_chat',
	REFINE_IN_STUDIO: 'refine_in_studio',
} as const;

/** Stable `flowKey` discriminants emitted by the BE on create-agent metadata. */
const FLOW_KEY = {
	ISSUE_ASSIGN: 'issue_assign',
	ISSUE_INVOKE: 'issue_invoke',
	BUILD_AND_CHAT: 'build_and_chat',
} as const;

/**
 * Flattens the BE's per-source nested `filters` shape into the flat shape that
 * `KnowledgePillList` expects.
 */
const toPillListKnowledge = (
	knowledgeSources?: StudioCreateAgentKnowledgeSourceConfig | null,
): Knowledge[] => {
	if (!knowledgeSources) return [];
	const flatSources = knowledgeSources.sources?.map((src) => {
		const nestedFilter = src?.filters
			? (Object.values(src.filters).find((f) => f !== null && f !== undefined) as
					| Record<string, unknown>
					| undefined)
			: undefined;
		return {
			...src,
			filters: nestedFilter ?? {},
		};
	});
	return [{ ...knowledgeSources, sources: flatSources }] as Knowledge[];
};

const mapToolsToCardTools = (tools?: StudioCreateAgentTool[] | null): Tool[] => {
	if (!tools?.length) {
		return [];
	}
	const result: Tool[] = [];
	for (let i = 0; i < tools.length; i++) {
		const tool = tools[i];
		const id = tool.toolId ?? tool.definitionId ?? `${tool.displayName ?? 'tool'}-${i}`;
		const displayName = tool.displayName ?? tool.toolId ?? tool.definitionId;
		if (!id || !displayName) {
			continue;
		}
		result.push({
			id,
			displayName,
			iconUrl: tool.iconUrl ?? undefined,
			tags: tool.tags ?? undefined,
			isActive: true,
		});
	}
	return result;
};

const getResultMessageFromExecutedData = (
	executedData?: StudioCreateAgentExecutedData,
): FollowUpResult | undefined => {
	if (executedData?.isSuccess === false) {
		return { type: 'create_failed' };
	}
	if (
		(executedData?.chosenOption === CHOSEN_OPTION.BUILD_AND_APPLY ||
			executedData?.chosenOption === CHOSEN_OPTION.BUILD_AND_CHAT) &&
		executedData.isPublished === false
	) {
		return { type: 'draft_publish_failed' };
	}
	return undefined;
};

export const StudioCreateAgentRenderer = (
	actionRenderer: ActionRendererFor<typeof ActionKeys.StudioCreateAgent>,
): React.JSX.Element | null => {
	di(useAssignAgent, useInvokeAgent, useAIMateExperienceTracker);

	const firstAction = actionRenderer.actions[0];
	const issueId = firstAction?.data?.agentMetadata?.jiraMetadata?.issueId;
	const flowKey = firstAction?.data?.agentMetadata?.jiraMetadata?.flowKey ?? undefined;

	const [isPrimaryActionLoading, setIsPrimaryActionLoading] = useState(false);
	const [isSecondaryActionLoading, setIsSecondaryActionLoading] = useState(false);
	const [followUpResult, setFollowUpResult] = useState<FollowUpResult | undefined>(undefined);

	const [agentAri, setAgentAri] = useState<string | undefined>(() => {
		const initial = firstAction?.executedData as StudioCreateAgentExecutedData | undefined;
		return initial?.agentAri ?? undefined;
	});
	const [confirmedChosenOption, setConfirmedChosenOption] = useState<string | undefined>(() => {
		const initial = firstAction?.executedData as StudioCreateAgentExecutedData | undefined;
		return initial?.chosenOption;
	});

	const [actionResultMessage, setActionResultMessage] = useState<FollowUpResult | undefined>(() =>
		getResultMessageFromExecutedData(firstAction?.executedData as StudioCreateAgentExecutedData),
	);
	const publishActionEvent = usePublish(Topics.AI_MATE_ACTIONS);
	const publishChatEvent = usePublish(Topics.AI_MATE);
	const { showFlag } = useFlags();
	const { formatMessage } = useIntl();
	const experienceTracker = useAIMateExperienceTracker();

	const { conversationId, messageId } = actionRenderer;
	const { invocationId } = firstAction ?? {};
	const agent = firstAction?.data?.agent;
	const isBuildAndChatFlow = flowKey === FLOW_KEY.BUILD_AND_CHAT;
	const isBuildAndChatMultiStepEnabled =
		isBuildAndChatFlow && fg('rovo_chat_multi_step_build_studio_create_agent');

	// The build-and-chat multi-step flow reaches the persisted card via two paths: the
	// primary "Build agent" (BUILD_AND_CHAT) and the secondary "Refine in Studio"
	// (REFINE_IN_STUDIO). Both create the agent, so both must surface the chat /
	// view-in-studio actions once it exists — not just the primary path.
	const isBuildAndChatAgentConfirmed =
		confirmedChosenOption === CHOSEN_OPTION.BUILD_AND_CHAT ||
		confirmedChosenOption === CHOSEN_OPTION.REFINE_IN_STUDIO;

	const { trackAgentEvent } = useRovoAgentActionAnalytics({
		touchPoint: GASV3_ANALYTICS_TOUCHPOINT_NAME,
		actionGroup: 'createFlow',
	});

	const { sendAnalyticsEvent } = useAnalytics();

	const isInvokeFlow = flowKey === FLOW_KEY.ISSUE_INVOKE;

	let ufoExperienceName: string = ExperienceName.STUDIO_CREATE_AGENT;
	switch (flowKey) {
		case FLOW_KEY.ISSUE_ASSIGN:
			ufoExperienceName = ExperienceName.STUDIO_CREATE_AGENT_ASSIGN;
			break;
		case FLOW_KEY.ISSUE_INVOKE:
			ufoExperienceName = ExperienceName.STUDIO_CREATE_AGENT_INVOKE;
			break;
		case FLOW_KEY.BUILD_AND_CHAT:
			ufoExperienceName = ExperienceName.STUDIO_CREATE_AGENT_BUILD_AND_CHAT;
			break;
		default:
			ufoExperienceName = ExperienceName.STUDIO_CREATE_AGENT;
	}

	const commonAttributes = useMemo(
		() => ({
			...(agent ? getPlanAnalyticsAttributes(agent) : {}),
			conversationId,
			invocationId,
			messageId,
			...getCreateAgentAnalyticsAttributes({ flowKey, issueId }),
		}),
		[agent, conversationId, invocationId, messageId, issueId, flowKey],
	);

	const { assignAgent } = useAssignAgent({
		conversationId,
		invocationId,
		messageId,
	});

	const { invokeAgent } = useInvokeAgent({
		conversationId,
		invocationId,
		messageId,
	});

	const fireConfirmAnalytics = useCallback(
		(result: StudioCreateAgentExecutedData) => {
			const analyticsAttributes = {
				...commonAttributes,
				...getPlanAnalyticsAttributes(agent),
				agentId: getAgentIdFromAri(result.agentAri) ?? undefined,
			};

			if (result.agentAri) {
				trackAgentEvent({
					action: 'createAgentRecord',
					actionSubject: 'rovoAgent',
					attributes: analyticsAttributes,
				});
			}
			if (result.isPublished) {
				trackAgentEvent({
					action: 'published',
					actionSubject: 'rovoAgent',
					attributes: {
						...analyticsAttributes,
						isFirstPublish: true,
						agentVersionNumber: 1,
					},
				});
			}
			if (result.isPublished) {
				trackAgentEvent({
					action: 'success',
					actionSubject: 'setIsAgentAssignable',
					attributes: {
						...analyticsAttributes,
						value: true,
					},
				});
			}
		},
		[trackAgentEvent, commonAttributes, agent],
	);

	const fireConfirmErrorAnalytics = useCallback(() => {
		trackAgentEvent({
			action: 'createFlowError',
			actionSubject: 'rovoAgent',
			attributes: {
				conversationId,
				invocationId,
				messageId,
				...getCreateAgentAnalyticsAttributes({ flowKey, issueId }),
			},
		});
	}, [trackAgentEvent, conversationId, invocationId, messageId, issueId, flowKey]);

	const confirmCreateAgent = useCallback(
		async ({
			chosenOption,
			shouldPublish,
		}: {
			chosenOption: string;
			shouldPublish: boolean;
		}): Promise<StudioCreateAgentExecutedData> => {
			if (!firstAction) return {};
			const payload = firstAction.data as StudioCreateAgentPayload;
			let result: StudioCreateAgentExecutedData;
			try {
				result =
					(await actionRenderer.onCustomDataConfirm<StudioCreateAgentExecutedData>(
						firstAction,
						{
							...payload,
							shouldPublish,
							chosenOption,
							isResultCustomMarkdown: true,
						},
						(message) => {
							const metadata = message.message_metadata as {
								createAgentResult?: StudioCreateAgentExecutedData;
							};
							return metadata.createAgentResult;
						},
					)) ?? {};
			} catch (error) {
				fireConfirmErrorAnalytics();
				throw error;
			}

			if (result.isSuccess === false) {
				fireConfirmErrorAnalytics();
			} else {
				fireConfirmAnalytics(result);
			}

			return result;
		},
		[actionRenderer, firstAction, fireConfirmAnalytics, fireConfirmErrorAnalytics],
	);

	const status = firstAction ? actionRenderer.getStatus(firstAction) : 'idle';
	const isReadonly = firstAction ? actionRenderer.getReadonly(firstAction) : false;
	const isSubmitting = status === 'started';

	const renderPrimaryAction = useCallback(
		({ isPublished }: { isPublished?: boolean } = {}) => {
			const shouldShowBuildAndChatPublishedActions =
				isBuildAndChatMultiStepEnabled &&
				agentAri &&
				isBuildAndChatAgentConfirmed &&
				isPublished === true;
			const shouldShowBuildAndChatDraftActions =
				isBuildAndChatMultiStepEnabled &&
				agentAri &&
				isBuildAndChatAgentConfirmed &&
				isPublished === false;

			if (shouldShowBuildAndChatPublishedActions) {
				const onStartChatAction = (
					_: React.MouseEvent<HTMLElement>,
					analyticsEvent: UIAnalyticsEvent,
				) => {
					sendAnalyticsEvent(
						analyticsEvent.update({
							actionSubjectId: 'startChatWithAgent',
							attributes: {
								analyticsSource: 'inline-create-agent-card',
								...commonAttributes,
							},
						}),
					);

					const agentId = getAgentIdFromAri(agentAri);
					if (!agentId) {
						setFollowUpResult({ type: 'build_and_chat_failed' });
						experienceTracker.fail({
							name: ufoExperienceName,
							error: new Error(`agentId not available for ${FLOW_KEY.BUILD_AND_CHAT} flow`),
							attributes: commonAttributes,
						});
						return;
					}

					publishChatEvent({
						type: 'chat-new',
						source: 'inline-agent-creation',
						data: {
							agentId,
							dialogues: [],
						},
					});

					setFollowUpResult({
						type: 'build_and_chat',
						agentName: agent?.name ?? '',
					});
				};

				return (
					<Button
						appearance="primary"
						iconBefore={AiChatIcon}
						isDisabled={isSubmitting || isPrimaryActionLoading || isSecondaryActionLoading}
						onClick={onStartChatAction}
						testId="rovo-create-agent-card-start-chat"
					>
						<FormattedMessage {...chatWithAgentLabel} />
					</Button>
				);
			}

			if (shouldShowBuildAndChatDraftActions) {
				return (
					<Button
						appearance="primary"
						iconBefore={AiChatIcon}
						isDisabled
						testId="rovo-create-agent-card-start-chat"
					>
						<FormattedMessage {...chatWithAgentLabel} />
					</Button>
				);
			}

			if (isBuildAndChatMultiStepEnabled && agentAri) {
				return (
					<Button appearance="primary" isDisabled testId="rovo-create-agent-card-build">
						<FormattedMessage {...buildAgentLabel} />
					</Button>
				);
			}

			// Per Tech Spec V2 "Actions Mapped to Flow":
			//   - `issue_assign` → primary `[Assign agent]`  → assignAgent
			//   - `issue_invoke` → primary `[Start session]` → invokeAgent
			//   - `build_and_chat` → primary `[Start chat]`  → open a new Rovo chat
			//     (or gated `[Build]` → publish first, then show `[Start chat]` on the published card)
			//   - fallthrough     → primary `[Build]`         → no follow-up
			const config = (() => {
				switch (flowKey) {
					case FLOW_KEY.ISSUE_ASSIGN:
						return {
							label: assignAgentLabel,
							testId: 'rovo-create-agent-card-assign-agent',
							chosenOption: CHOSEN_OPTION.BUILD_AND_APPLY,
							shouldPublish: true,
							followUp: assignAgent,
							shouldPublishAssignedEvent: true,
							actionSubjectId: 'buildAndAssignAgent',
							shouldOpenChat: false,
						};
					case FLOW_KEY.ISSUE_INVOKE:
						return {
							label: buildAndInvokeLabel,
							testId: 'rovo-create-agent-card-start-session',
							chosenOption: CHOSEN_OPTION.BUILD_AND_APPLY,
							shouldPublish: true,
							followUp: invokeAgent,
							shouldPublishAssignedEvent: false,
							actionSubjectId: 'buildAndInvokeAgent',
							shouldOpenChat: false,
						};
					case FLOW_KEY.BUILD_AND_CHAT:
						return {
							label: isBuildAndChatMultiStepEnabled ? buildAgentLabel : buildAndChatLabel,
							testId: isBuildAndChatMultiStepEnabled
								? 'rovo-create-agent-card-build'
								: 'rovo-create-agent-card-start-chat',
							chosenOption: CHOSEN_OPTION.BUILD_AND_CHAT,
							shouldPublish: true,
							followUp: undefined,
							shouldPublishAssignedEvent: false,
							actionSubjectId: 'buildAndChatAgent',
							shouldOpenChat: !isBuildAndChatMultiStepEnabled,
						};
					default:
						return {
							label: buildLabel,
							testId: 'rovo-create-agent-card-build',
							chosenOption: CHOSEN_OPTION.BUILD_DRAFT,
							shouldPublish: false,
							followUp: undefined,
							shouldPublishAssignedEvent: false,
							actionSubjectId: 'buildAgent',
							shouldOpenChat: false,
						};
				}
			})();

			// Draft state for fallback non-Jira issue flow.
			if (!flowKey && agentAri) {
				return undefined;
			}

			const onPrimaryAction = async (
				_: React.MouseEvent<HTMLElement>,
				analyticsEvent: UIAnalyticsEvent,
			) => {
				sendAnalyticsEvent(
					analyticsEvent.update({
						actionSubjectId: config.actionSubjectId,
						attributes: {
							analyticsSource: 'inline-create-agent-card',
							...commonAttributes,
						},
					}),
				);
				setIsPrimaryActionLoading(true);
				let didSetActionResultMessage = false;
				try {
					if (isReadonly) return;

					experienceTracker.start({
						id: ufoExperienceName,
						name: ufoExperienceName,
						attributes: commonAttributes,
					});

					const result = await confirmCreateAgent({
						chosenOption: config.chosenOption,
						shouldPublish: config.shouldPublish,
					});

					if (result.agentAri) {
						setAgentAri(result.agentAri);
					}

					if (result.isSuccess === false) {
						didSetActionResultMessage = true;
						setActionResultMessage({ type: 'create_failed' });
						throw new Error('Agent creation failed');
					}
					if (config.shouldPublish && result.isPublished === false) {
						didSetActionResultMessage = true;
						setActionResultMessage({ type: 'draft_publish_failed' });
						throw new Error('Agent draft publish failed');
					}
					setConfirmedChosenOption(config.chosenOption);

					if (config.shouldOpenChat) {
						const agentId = getAgentIdFromAri(result.agentAri);
						if (!agentId) {
							setFollowUpResult({ type: 'build_and_chat_failed' });
							experienceTracker.fail({
								name: ufoExperienceName,
								error: new Error(`agentId not available for ${FLOW_KEY.BUILD_AND_CHAT} flow`),
								attributes: commonAttributes,
							});
							return;
						}

						publishChatEvent({
							type: 'chat-new',
							source: 'inline-agent-creation',
							data: {
								agentId,
								dialogues: [],
							},
						});

						setFollowUpResult({
							type: 'build_and_chat',
							agentName: agent?.name ?? '',
						});

						experienceTracker.succeed({
							name: ufoExperienceName,
							attributes: {
								...commonAttributes,
								agentId,
							},
						});

						return;
					}

					if (!config.followUp) {
						// Mainly for fallback non-Jira issue flow where we don't have any follow-up.
						experienceTracker.succeed({
							name: ufoExperienceName,
							attributes: {
								...commonAttributes,
								agentId: getAgentIdFromAri(result.agentAri) ?? undefined,
							},
						});

						// Redirect fallback drafts to Studio so the user can move the agent out of draft.
						if (!flowKey && fg('sltns-1247-redirect-on-agent-build')) {
							if (result.agentAri) {
								const agentId = getAgentIdFromAri(result.agentAri);
								if (agentId) {
									const editUrl = getAtlassianStudioAgentEditUrl(
										actionRenderer.cloudId,
										agentId,
										STUDIO_NAVIGATION_SOURCE,
									);
									window.location.assign(editUrl);
								}
							}
						}

						return;
					}

					// Show an info flag while assigning (assign flow only) — assign can take up to 90s.
					if (flowKey === FLOW_KEY.ISSUE_ASSIGN) {
						showFlag({
							testId: 'rovo-agent-assigning',
							title: formatMessage(assigningFlagTitle),
							description: formatMessage(assigningFlagDescription),
							isAutoDismiss: true,
							// Assign can take up to 90 seconds — keep the info flag visible for that duration.
							autoDismissSeconds: 90,
						});
					}

					try {
						if (!result.identityAccountId)
							throw new Error(`identityAccountId not available for ${flowKey} flow`);

						if (issueId) {
							await config.followUp({
								cloudId: actionRenderer.cloudId,
								issueId,
								identityAccountId: result.identityAccountId,
								agentId: getAgentIdFromAri(result.agentAri),
							});
							// Follow-up mutation succeeded — show contextual success text below the card.
							setFollowUpResult({
								type: isInvokeFlow ? 'invoke' : 'assigned',
								agentName: agent?.name ?? '',
							});
						}

						experienceTracker.succeed({
							name: ufoExperienceName,
							attributes: {
								...commonAttributes,
								agentId: getAgentIdFromAri(result.agentAri) ?? undefined,
							},
						});
						// Follow-up mutation succeeded — show contextual success text below the card.
						setFollowUpResult({
							type: flowKey === FLOW_KEY.ISSUE_INVOKE ? 'invoke' : 'assigned',
							agentName: agent?.name ?? '',
						});

						// Show success flag (assign flow only).
						if (flowKey === FLOW_KEY.ISSUE_ASSIGN) {
							showFlag({
								testId: 'rovo-agent-assigned',
								appearance: 'success',
								title: formatMessage(assignedFlagTitle),
								description: formatMessage(assignedFlagDescription),
								isAutoDismiss: true,
							});
						}

						if (config.shouldPublishAssignedEvent && issueId) {
							publishActionEvent({
								type: JIRA_INLINE_AGENT_CREATION_AGENT_ASSIGNED_EVENT,
								source: 'inline-agent-creation',
								data: {
									issueId,
								},
							});
						}
					} catch (followUpError) {
						// Follow-up mutation failed — agent was built but assign/invoke did not complete.
						setFollowUpResult({
							type: isInvokeFlow ? 'invoke_failed' : 'assign_failed',
						});
						const followUpErr =
							followUpError instanceof Error ? followUpError : new Error(String(followUpError));
						const statusCode =
							followUpError instanceof AssignAgentError ? followUpError.statusCode : undefined;
						// 4xx errors are expected/known conditions — abort rather than fail the experience.
						if (statusCode !== undefined && statusCode >= 400 && statusCode < 500) {
							experienceTracker.abort({
								name: ufoExperienceName,
								reason: `Failed with status ${statusCode}`,
								attributes: { ...commonAttributes, statusCode },
							});
						} else {
							experienceTracker.fail({
								name: ufoExperienceName,
								error: followUpErr,
								attributes: {
									...commonAttributes,
									...(statusCode !== undefined && { statusCode }),
								},
							});
						}
					}
				} catch {
					// confirmCreateAgent itself failed — analytics already fired in confirmCreateAgent.
					if (config.shouldOpenChat && !didSetActionResultMessage) {
						setFollowUpResult({ type: 'build_and_chat_failed' });
					}
					experienceTracker.fail({
						name: ufoExperienceName,
						error: new Error('confirmCreateAgent failed'),
						attributes: commonAttributes,
					});
				} finally {
					setIsPrimaryActionLoading(false);
				}
			};
			return (
				<Button
					appearance="primary"
					iconBefore={config.shouldOpenChat ? AiChatIcon : undefined}
					isLoading={isPrimaryActionLoading}
					isDisabled={
						!!agentAri ||
						isReadonly ||
						isSubmitting ||
						isPrimaryActionLoading ||
						isSecondaryActionLoading
					}
					onClick={onPrimaryAction}
					testId={config.testId}
				>
					<FormattedMessage {...config.label} />
				</Button>
			);
		},
		[
			flowKey,
			agentAri,
			isReadonly,
			isSubmitting,
			isPrimaryActionLoading,
			isSecondaryActionLoading,
			isBuildAndChatMultiStepEnabled,
			confirmCreateAgent,
			assignAgent,
			invokeAgent,
			issueId,
			actionRenderer.cloudId,
			publishActionEvent,
			publishChatEvent,
			sendAnalyticsEvent,
			agent,
			setFollowUpResult,
			showFlag,
			formatMessage,
			experienceTracker,
			commonAttributes,
			isInvokeFlow,
			ufoExperienceName,
			isBuildAndChatAgentConfirmed,
		],
	);

	const primaryAction = useMemo(() => renderPrimaryAction(), [renderPrimaryAction]);

	const renderSecondaryAction = useCallback(
		({ isPublished }: { isPublished?: boolean } = {}) => {
			if (!firstAction) return undefined;

			const shouldShowBuildAndChatPublishedActions =
				isBuildAndChatMultiStepEnabled &&
				agentAri &&
				isBuildAndChatAgentConfirmed &&
				isPublished === true;

			const refineInStudioFlowKey =
				flowKey === FLOW_KEY.ISSUE_ASSIGN ||
				flowKey === FLOW_KEY.ISSUE_INVOKE ||
				flowKey === FLOW_KEY.BUILD_AND_CHAT
					? flowKey
					: undefined;

			if (!agentAri && !refineInStudioFlowKey) return undefined;

			const onSecondaryAction = async (
				_: React.MouseEvent<HTMLElement>,
				analyticsEvent: UIAnalyticsEvent,
			) => {
				sendAnalyticsEvent(
					analyticsEvent.update({
						actionSubjectId: 'refineInStudio',
						attributes: {
							analyticsSource: 'inline-create-agent-card',
							conversationId,
							invocationId,
							messageId,
							...getCreateAgentAnalyticsAttributes({ flowKey, issueId }),
							...getPlanAnalyticsAttributes(agent),
						},
					}),
				);
				setIsSecondaryActionLoading(true);
				try {
					let nextAri = agentAri;
					if (!nextAri && !isReadonly) {
						experienceTracker.start({
							id: ExperienceName.STUDIO_CREATE_AGENT_REFINE_IN_STUDIO,
							name: ExperienceName.STUDIO_CREATE_AGENT_REFINE_IN_STUDIO,
							attributes: commonAttributes,
						});

						const result = await confirmCreateAgent({
							chosenOption: CHOSEN_OPTION.REFINE_IN_STUDIO,
							shouldPublish: false,
						});
						if (result.isSuccess === false) {
							setActionResultMessage({ type: 'create_failed' });
							throw new Error('Agent creation failed');
						}
						if (!result.agentAri) {
							throw new Error('agentAri not available for refine in studio flow');
						}

						experienceTracker.succeed({
							name: ExperienceName.STUDIO_CREATE_AGENT_REFINE_IN_STUDIO,
							attributes: {
								...commonAttributes,
								agentId: getAgentIdFromAri(result.agentAri) ?? undefined,
							},
						});

						setAgentAri(result.agentAri);
						setConfirmedChosenOption(CHOSEN_OPTION.REFINE_IN_STUDIO);
						nextAri = result.agentAri;
					}
					const agentId = getAgentIdFromAri(nextAri);
					if (!agentId) return;
					const editUrl = getAtlassianStudioAgentEditUrl(
						actionRenderer.cloudId,
						agentId,
						STUDIO_NAVIGATION_SOURCE,
					);
					window.open(editUrl, '_blank', 'noopener,noreferrer');
					if (refineInStudioFlowKey && !shouldShowBuildAndChatPublishedActions) {
						// Show contextual follow-up text so the user knows what to do next in Studio.
						setFollowUpResult({
							type: 'refine_in_studio',
							flowKey: refineInStudioFlowKey,
						});
					}
				} catch (error) {
					// confirmCreateAgent failed — analytics already fired in confirmCreateAgent.
					experienceTracker.fail({
						name: ExperienceName.STUDIO_CREATE_AGENT_REFINE_IN_STUDIO,
						error: error instanceof Error ? error : new Error(String(error)),
						attributes: commonAttributes,
					});
				} finally {
					setIsSecondaryActionLoading(false);
				}
			};
			const secondaryActionLabel = shouldShowBuildAndChatPublishedActions
				? viewInStudioLabel
				: refineInStudioLabel;
			return (
				<Button
					appearance="default"
					iconAfter={LinkExternalIcon}
					isLoading={isSecondaryActionLoading}
					isDisabled={isSubmitting || isPrimaryActionLoading || isSecondaryActionLoading}
					onClick={onSecondaryAction}
					testId="rovo-create-agent-card-refine-in-studio"
				>
					<FormattedMessage {...secondaryActionLabel} />
				</Button>
			);
		},
		[
			firstAction,
			flowKey,
			isReadonly,
			isSubmitting,
			isPrimaryActionLoading,
			isSecondaryActionLoading,
			agentAri,
			confirmCreateAgent,
			actionRenderer.cloudId,
			sendAnalyticsEvent,
			conversationId,
			invocationId,
			messageId,
			issueId,
			agent,
			setFollowUpResult,
			experienceTracker,
			commonAttributes,
			isBuildAndChatMultiStepEnabled,
			isBuildAndChatAgentConfirmed,
		],
	);

	const secondaryAction = useMemo(() => renderSecondaryAction(), [renderSecondaryAction]);

	const handleRenderError = useCallback(
		(error: Error) => {
			experienceTracker.fail({
				name: ExperienceName.STUDIO_CREATE_AGENT_RENDER,
				error,
				attributes: commonAttributes,
			});
		},
		[experienceTracker, commonAttributes],
	);

	if (!firstAction || !agent) return null;

	const cardProps = {
		cloudId: actionRenderer.cloudId,
		agentName: agent.name,
		description: agent.description ?? '',
		instructions: agent.instructions ?? '',
		knowledge: toPillListKnowledge(agent.knowledgeSources),
		tools: mapToolsToCardTools(agent.tools),
		primaryAction,
		secondaryAction,
		renderExperienceName: ExperienceName.STUDIO_CREATE_AGENT_RENDER,
		shouldRenderDetailedPublishedCard: isBuildAndChatMultiStepEnabled,
	};

	if (!agentAri) {
		return (
			<>
				<ExperienceStart
					name={ExperienceName.STUDIO_CREATE_AGENT_RENDER}
					attributes={{ ...commonAttributes, state: 'plan' }}
				/>
				<ErrorBoundary
					fallback={<ChatGenericError errorType="generic" />}
					onError={handleRenderError}
				>
					<AgentPlanAnalytics actionRenderer={actionRenderer} />
					<CreateAgentCard {...cardProps} />
					{actionResultMessage && (
						<AgentCreationFollowUpText followUpResult={actionResultMessage} />
					)}
					<AgentCreationFollowUpText followUpResult={followUpResult} />
				</ErrorBoundary>
			</>
		);
	}

	// For draft/published states fetch the persisted agent so we render
	// hydrated values (name, description, instructions, icon, publish status).
	return (
		<>
			<ExperienceStart
				name={ExperienceName.STUDIO_CREATE_AGENT_RENDER}
				attributes={{ ...commonAttributes, state: 'persisted' }}
			/>
			<ErrorBoundary
				fallback={<ChatGenericError errorType="generic" />}
				onError={handleRenderError}
			>
				<Suspense fallback={<CreateAgentCardSkeleton />}>
					<CreateAgentCardWithQuery
						agentAri={agentAri}
						{...cardProps}
						renderPrimaryAction={renderPrimaryAction}
						renderSecondaryAction={renderSecondaryAction}
					/>
				</Suspense>
				{actionResultMessage && <AgentCreationFollowUpText followUpResult={actionResultMessage} />}
				<AgentCreationFollowUpText followUpResult={followUpResult} />
			</ErrorBoundary>
		</>
	);
};
